import { Unit } from '@enmon/commons';
import { NotificationType, AnalysisCalculation, Granularity, Period } from '@enmon/commons/lib/enmon';
import { Commodity } from '@enmon/commons/lib/shared';
import type { BackendQuery } from '@/types/BackendQuery';
import { createAction } from '@reduxjs/toolkit';

export enum ModalStep {
  NotificationType = 'NotificationType',
  Threshold = 'Threshold',
  Recipients = 'Recipients',
}

export type NotificationConfigShape = {
  unit: Unit;
  type: NotificationType;
};

export const DEFAULT_THRESHOLD_MULTIPLIER = 1.1;

export const COMMODITY_NOTIFICATIONS_CONFIG: Record<Commodity, NotificationConfigShape[]> = {
  [Commodity.Electricity]: [
    {
      unit: Unit.kW,
      type: NotificationType.QuarterhourMax,
    },
    {
      unit: Unit.kWh,
      type: NotificationType.BusinesshourMax,
    },
    {
      unit: Unit.kWh,
      type: NotificationType.NonbusinesshourMax,
    },
    { unit: Unit.kWh, type: NotificationType.DayMax },
  ],
  [Commodity.NaturalGas]: [
    {
      unit: Unit.m3,
      type: NotificationType.BusinesshourMax,
    },
    {
      unit: Unit.m3,
      type: NotificationType.NonbusinesshourMax,
    },
    { unit: Unit.m3, type: NotificationType.DayMax },
  ],
  [Commodity.Thermal]: [
    {
      unit: Unit.kWh,
      type: NotificationType.BusinesshourMax,
    },
    {
      unit: Unit.kWh,
      type: NotificationType.NonbusinesshourMax,
    },
    { unit: Unit.kWh, type: NotificationType.DayMax },
  ],
  [Commodity.Water]: [
    {
      unit: Unit.m3,
      type: NotificationType.BusinesshourMax,
    },
    {
      unit: Unit.m3,
      type: NotificationType.NonbusinesshourMax,
    },
    { unit: Unit.m3, type: NotificationType.DayMax },
  ],
  [Commodity.Other]: [
    {
      unit: Unit.celsius,
      type: NotificationType.BusinesshourOver,
    },
    {
      unit: Unit.celsius,
      type: NotificationType.BusinesshourBelow,
    },
    {
      unit: Unit.celsius,
      type: NotificationType.NonbusinesshourOver,
    },
    {
      unit: Unit.celsius,
      type: NotificationType.NonbusinesshourBelow,
    },
  ],
};

export const SHARED_NOTIFICATIONS = [NotificationType.NoMessage, NotificationType.MissingManualReading] as const;

export const PAGE_SIZE = 20 as const;

export const CONSUMPTION_NOTIFICATIONS = [
  NotificationType.BusinesshourMax,
  NotificationType.NonbusinesshourMax,
  NotificationType.DayMax,
] as const;

export const CONSUMPTION_ANALYSIS_CONFIG: Record<string, BackendQuery> = {
  [NotificationType.DayMax]: {
    period: { predefined: Period.Last30days },
    granularity: Granularity.Day,
  },
  [NotificationType.BusinesshourMax]: {
    period: { predefined: Period.Last7days },
    granularity: Granularity.Hour,
  },
  [NotificationType.NonbusinesshourMax]: {
    period: { predefined: Period.Last7days },
    granularity: Granularity.Hour,
  },
  [NotificationType.QuarterhourMax]: {
    period: { predefined: Period.Last3months },
    granularity: Granularity.Day,
    maximumInterval: Granularity.Quarterhour,
  },
};

export const VALUES_NOTIFICATION = [
  NotificationType.BusinesshourOver,
  NotificationType.BusinesshourBelow,
  NotificationType.NonbusinesshourOver,
  NotificationType.NonbusinesshourBelow,
] as const;

export const VALUE_ANALYSIS_CONFIG: Record<string, BackendQuery> = {
  [NotificationType.BusinesshourBelow]: {
    period: { predefined: Period.Last7days },
    granularity: Granularity.Hour,
    calculation: AnalysisCalculation.Min,
  },
  [NotificationType.BusinesshourOver]: {
    period: { predefined: Period.Last7days },
    granularity: Granularity.Hour,
    calculation: AnalysisCalculation.Max,
  },
  [NotificationType.NonbusinesshourBelow]: {
    period: { predefined: Period.Last7days },
    granularity: Granularity.Hour,
    calculation: AnalysisCalculation.Min,
  },
  [NotificationType.NonbusinesshourOver]: {
    period: { predefined: Period.Last7days },
    granularity: Granularity.Hour,
    calculation: AnalysisCalculation.Max,
  },
} as const;

export const METER_NOTIFICATION_SETTING_FORM_ID = 'meterNotificationSettingForm';

export const saveNotificationSettingSucceeded = createAction(
  `${METER_NOTIFICATION_SETTING_FORM_ID}/saveNotificationSettingSucceeded`,
);

export const saveNotificationSettingFailed = createAction(
  `${METER_NOTIFICATION_SETTING_FORM_ID}/saveNotificationSettingFailed`,
);

export const BUSINESS_HOURS_NOTIFICATIONS = [
  NotificationType.BusinesshourBelow,
  NotificationType.BusinesshourOver,
  NotificationType.BusinesshourMax,
  NotificationType.NonbusinesshourBelow,
  NotificationType.NonbusinesshourOver,
  NotificationType.NonbusinesshourMax,
] as const;
